// @ts-strict-ignore
import type { CoordinateExtent } from 'react-flow-renderer';
import type { WorkflowState } from 'flow/store/redux/state';
import type { Bounds, CanvasSize } from '../layout/types';
import type { Graph } from '../types';
import {
  CANVAS_HORIZONTAL_PADDING,
  CANVAS_PANNING_PADDING_VERTICAL,
  CANVAS_VERTICAL_PADDING,
  COMPLEX_ISLAND_WIDTH,
  IN_BOUNDS_PADDING_WIDTH,
  NODE_HEIGHT,
  NODE_WIDTH,
  OUT_OF_BOUNDS_PADDING_WIDTH,
  REQUIRED_SPACE_FOR_ISLAND_NODES,
  SPACE_FOR_NODE_EDITOR_DRAWER,
  TOTAL_REQUIRED_SPACE_FOR_SIMPLE_NODES_X,
} from '../layout';

/**
 * Creates an imaginary box that wraps the graph plus horizontal and vertical padding
 */
export function getGraphBounds(graph: Graph): Bounds {
  // Infinity initializers guarantee a change on the first comparison
  const bounds: Bounds = {
    top: Infinity,
    bottom: -Infinity,
    left: Infinity,
    right: -Infinity,
  };

  graph.nodes.forEach(node => {
    /**
     * since outcomes & workflow routing nodes are all invisible we don't
     * consider them for the graph bounds calculation
     */
    if (node.type === 'outcome' || node.type === 'workflow') return;
    const top = node.position.y;
    const bottom = top + NODE_HEIGHT;
    const left = node.position.x;
    const right = left + NODE_WIDTH;

    bounds.top = Math.min(bounds.top, top);
    bounds.bottom = Math.max(bounds.bottom, bottom);
    bounds.left = Math.min(bounds.left, left);
    bounds.right = Math.max(bounds.right, right);
  });


  // catch edge cases where the graph is empty or only has outcome nodes
  const isBoundUnchanged = Object.values(bounds).some(bound => Math.abs(bound) === Infinity);
  if (isBoundUnchanged) {
    return {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    };
  }
  return bounds;
}

/**
 * returns the limits or bounds the canvas is allowed to scroll within
 * (see README.md for more info)
 */
export function getCanvasTranslateExtent({
  canvasSize,
  graphBounds,
  isComplexIslandOpen,
  isNodeEditorDrawerOpen,
}: {
  canvasSize: {
    width: WorkflowState['ui']['canvasProperties']['rect']['width'];
    height: WorkflowState['ui']['canvasProperties']['rect']['height'];
  };
  graphBounds: Bounds;
  isComplexIslandOpen: boolean;
  isNodeEditorDrawerOpen: boolean;
}): CoordinateExtent {
  const isThereContentOutsideOfTheCanvasX = (
    isComplexIslandOpen
      ? ((canvasSize.width - COMPLEX_ISLAND_WIDTH) < graphBounds.right)
      : graphBounds.right > canvasSize.width
  );

  const isThereContentOutsideOfTheCanvasY = graphBounds.bottom > canvasSize.height;

  const rightLimit = isThereContentOutsideOfTheCanvasX
    ? graphBounds.right
    : canvasSize.width;

  const bottomLimit = isThereContentOutsideOfTheCanvasY
    ? graphBounds.bottom + CANVAS_PANNING_PADDING_VERTICAL
    : canvasSize.height;

  const isThereSpaceForOutcomeIslandNodes = (
    (graphBounds.right
      + TOTAL_REQUIRED_SPACE_FOR_SIMPLE_NODES_X) < canvasSize.width
  );

  const isDrawerBlockingGraph = isNodeEditorDrawerOpen
    ? (graphBounds.bottom + SPACE_FOR_NODE_EDITOR_DRAWER) > bottomLimit
    : false;

  const requiredSpaceForIslandX = (
    isThereContentOutsideOfTheCanvasX
      ? OUT_OF_BOUNDS_PADDING_WIDTH
      // We double the column spacing because just 1 column worth only gives
      // just enough room to see the link indicator
      : IN_BOUNDS_PADDING_WIDTH
  );

  const x0 = 0;
  const y0 = 0;
  const x1 = (
    isThereSpaceForOutcomeIslandNodes && !isComplexIslandOpen
      ? rightLimit
      : rightLimit + requiredSpaceForIslandX
  );

  const y1 = isDrawerBlockingGraph
    // SPACE_FOR_NODE_EDITOR_DRAWER works but it could still be fine tuned
    // to only leave CANVAS_PANNING_PADDING_VERTICAL of space in the future
    ? bottomLimit + SPACE_FOR_NODE_EDITOR_DRAWER
    : bottomLimit;

  const translateExtent: CoordinateExtent = [
    [x0, y0],
    [x1, y1],
  ];

  return translateExtent;
}


/**
 * returns wether the outcome island should be expanded or peeking
 * based on the available space, position and graph elements
 * @todo - this function might be moved somewhere else
 */
export function getSimpleIslandMode(
  graphBounds: Bounds,
  canvasRect: WorkflowState['ui']['canvasProperties']['rect'],
): 'peeking' | 'expanded' {
  return (
    canvasRect.x
    + graphBounds.right
    + REQUIRED_SPACE_FOR_ISLAND_NODES
    + CANVAS_HORIZONTAL_PADDING
  ) < canvasRect.width ? 'expanded' : 'peeking';
}


/**
 * returns whether the graph bounds fit on screen, taking
 * island and padding sizes into consideration.
 */
export function isGraphRequiredSpaceLargerThanCanvas(
  graphBounds: Bounds,
  canvasSize: CanvasSize,
) {
  const isLargerThanCanvasX = (
    graphBounds.right
    + TOTAL_REQUIRED_SPACE_FOR_SIMPLE_NODES_X
  ) > canvasSize.width;

  const isLargerThanCanvasY = (
    graphBounds.bottom
    + CANVAS_VERTICAL_PADDING
  ) > canvasSize.height;


  return isLargerThanCanvasX || isLargerThanCanvasY;
}
